import { Analytics } from '@easy-expense/analytics-client';
import { getPlaidLinkSession } from '@easy-expense/auth-client';
import { useAuthStore, usePlaidLink } from '@easy-expense/data-firestore-client';
import { entityFields } from '@easy-expense/data-firestore-shared';
import { PendingLinkSession, PendingLinkSessionSchema } from '@easy-expense/data-schema-v2';
import React from 'react';
import { usePlaidLink as usePlaid } from 'react-plaid-link';

// remove the '<web-onboarding>' orgID flag if we want to use elsewhere on web
export default function usePlaidWebLink() {
  const [session, setSession] = React.useState<PendingLinkSession | null>(null);
  const { startVerify } = usePlaidLink(session);
  React.useEffect(() => {
    if (session) {
      startVerify();
    }
  }, [session]);

  const user = useAuthStore.use.currentUser();

  const [linkToken, setLinkToken] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!user) {
      return;
    }
    const fetchToken = async () => {
      const res = await getPlaidLinkSession();
      setLinkToken(res.link_token);
    };
    fetchToken();
  }, [user]);

  const {
    open: openPlaid,
    ready,
    error,
  } = usePlaid({
    token: linkToken!,
    onSuccess: (public_token, metadata) => {
      Analytics.track('bank_link_succeeded', {
        mode: 'new',
        institution_id: metadata.institution?.institution_id,
        institution_name: metadata.institution?.name,
        account_count: metadata.accounts.length,
      });
      setSession(
        PendingLinkSessionSchema.parse({
          publicToken: public_token,
          metadata: {
            institution: {
              id: metadata.institution?.institution_id,
              name: metadata.institution?.name,
            },
            accounts: metadata.accounts.map((acc: any) => ({
              ...acc,
              _id: acc.id,
              orgID: '<web-onboarding>',
              meta: {
                name: acc.name,
                number: acc.mask,
              },
            })),
          },
          ...entityFields('create', user?.uid ?? ''),
          product: 'transactions',
        }),
      );
    },
    onExit: (err, metadata) => {
      Analytics.track('bank_link_exited', {
        mode: 'new',
        link_session_id: metadata.link_session_id,
        request_id: metadata.request_id,
        exit_status: metadata.status ?? undefined,
        error_code: err?.error_code,
        error_type: err?.error_type,
        institution_id: metadata.institution?.institution_id,
        institution_name: metadata.institution?.name,
      });
    },
  });

  const open = React.useCallback(() => {
    Analytics.track('bank_link_entry_tapped', { location: 'web_onboarding', mode: 'new' });
    Analytics.track('bank_link_opened', { mode: 'new' });
    openPlaid();
  }, [openPlaid]);

  return { linkToken, open, ready, error, session };
}
